/**
 * Whether the offline cache is still able to hold anything.
 *
 * Kept apart from `TrackingAvailability` on purpose. A full cache does not stop
 * a reader tracking episodes -- those writes go to the server -- it only stops
 * the copy that makes the app work offline later. Folding it into the
 * availability enum would have blocked mutations that were never in danger.
 *
 * A module-level store rather than context: cache writes happen in plain async
 * functions far from any component, and threading a setter down to them would
 * mean every caller in the offline layer taking a React dependency.
 */

export type CacheHealth = "ok" | "full";

let health: CacheHealth = "ok";
const listeners = new Set<() => void>();

export function readCacheHealth(): CacheHealth {
  return health;
}

function publish(next: CacheHealth) {
  if (health === next) return;
  health = next;
  for (const listener of listeners) listener();
}

/** Called when a write failed for want of room, not for want of correctness. */
export function reportCacheFull() {
  publish("full");
}

/**
 * Called after a write succeeds.
 *
 * Recovery matters as much as detection: a reader who clears space should stop
 * being told storage is full without having to reload the app.
 */
export function reportCacheWritable() {
  publish("ok");
}

export function subscribeCacheHealth(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam. Module state outlives a single test otherwise. */
export function resetCacheHealth() {
  health = "ok";
  listeners.clear();
}
